import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';

import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { pick } from 'lodash';
import { type FC, MouseEvent, useEffect, useMemo, useState } from 'react';
import GridLayout from 'react-grid-layout';
import { DataTestID, lang } from 'src/utils';
import { uid } from 'uid';

import {
  calculateIsGridFull,
  calculateOccupiedCells,
  calculateSectionDimensions,
  getHighlightedCells,
  isAreaOccupied,
} from './LayoutBuilder.helper';
import {
  type LayoutBuilderProps,
  type LayoutGridCell,
} from './LayoutBuilder.interface';
import {
  DeleteButton,
  GridCell,
  GridContainer,
  GridItem,
  GridRow,
} from './LayoutBuilder.styled';

const GRID_CELL_HEIGHT_ADJUSTMENT = 10; // Threshold to keep elements within grid

export const LayoutBuilder: FC<LayoutBuilderProps> = ({
  gridSections = [],
  setGridSections,
  cols = 3,
  rows = 4,
  cellWidth = 120,
  cellHeight = 145,
  dynamicRatio = 1,
  isRecommendationEnabled,
  onActiveSectionChange,
  onGridSectionsChange,
  canDeleteCurrentSelectedSection,
  sectionFontSize,
  isScheduleComponent,
  transformScale = 1,
}) => {
  const [isDrawing, setIsDrawing] = useState(false);
  const [startPos, setStartPos] = useState<LayoutGridCell | null>(null);
  const [highlightedCells, setHighlightedCells] = useState<LayoutGridCell[]>(
    [],
  );
  const [isGridFull, setIsGridFull] = useState(false);
  const [activeSection, setActiveSection] = useState<string | null>(null);

  // Calculate occupied cells
  const occupiedCells = useMemo(() => {
    return calculateOccupiedCells(gridSections, cols, rows);
  }, [gridSections, cols, rows]);

  // Check if grid is full
  useEffect(() => {
    setIsGridFull(calculateIsGridFull(gridSections, cols, rows));
  }, [gridSections, cols, rows]);

  // Handle mouse down to start drawing
  const handleMouseDown = (col: number, row: number) => {
    if (occupiedCells[row][col]) return; // Prevent drawing on occupied cells
    setStartPos({ col, row });
    setIsDrawing(true);
    setActiveSection(null);
    onActiveSectionChange?.(null);
    setHighlightedCells([{ col, row }]); // Start highlighting the initial cell
  };

  // Handle mouse move to highlight cells while drawing
  const handleMouseMove = (col: number, row: number) => {
    if (!isDrawing || !startPos) return;

    const newHighlightedCells = getHighlightedCells(startPos, col, row);
    setHighlightedCells(newHighlightedCells);
  };

  // Handle mouse up to finish drawing the section
  const handleMouseUp = (col: number, row: number) => {
    if (!isDrawing || !startPos) return;
    setIsDrawing(false);

    const { x, y, w, h } = calculateSectionDimensions(startPos, col, row);

    // Check if the new section will intersect with any occupied cells
    if (isAreaOccupied(occupiedCells, x, y, w, h)) {
      setHighlightedCells([]); // Clear highlights

      return;
    }

    // Add a new section to the layout
    const newSectionId = uid();
    const newSection = [
      ...gridSections,
      {
        i: newSectionId,
        x,
        y,
        w,
        h,
      },
    ];

    setActiveSection(newSectionId);
    onActiveSectionChange?.(newSectionId);
    setGridSections(newSection);
    onGridSectionsChange?.(newSection);
    setHighlightedCells([]);
  };

  // Handle delete section
  const handleDelete = (id: string) => {
    const curData = gridSections.filter((item) => item.i !== id);

    setGridSections(curData);
    onGridSectionsChange?.(curData);
    setActiveSection(null);
    onActiveSectionChange?.(null);
  };

  // Handle mouse leave to cancel highlight
  const handleMouseLeave = () => {
    if (isDrawing) {
      setHighlightedCells([]);
      setIsDrawing(false);
    }
  };

  return (
    <GridContainer
      data-testid={DataTestID.LayoutBuilder}
      onMouseLeave={handleMouseLeave}>
      {/* Render Grid Cells */}
      {Array.from({ length: rows }).map((_, row) => (
        <GridRow key={row}>
          {Array.from({ length: cols }).map((_, col) => (
            <GridCell
              key={col}
              data-testid={`${DataTestID.LayoutBuilderGridCell}-${row}-${col}`}
              role={'gridcell'}
              $cellWidth={cellWidth}
              $cellHeight={cellHeight}
              $dynamicRatio={dynamicRatio}
              $highlighted={highlightedCells.some(
                (cell) => cell.col === col && cell.row === row,
              )}
              $occupied={occupiedCells[row][col]} // Track occupied state
              onMouseDown={() => handleMouseDown(col, row)}
              onMouseMove={() => handleMouseMove(col, row)}
              onMouseUp={() => handleMouseUp(col, row)}
            />
          ))}
        </GridRow>
      ))}

      {/* Render Sections using react-grid-layout */}
      <GridLayout
        className="layout"
        layout={gridSections}
        cols={cols}
        rowHeight={cellHeight - GRID_CELL_HEIGHT_ADJUSTMENT}
        width={cols * cellWidth}
        maxRows={rows}
        isDraggable={false}
        transformScale={transformScale}
        compactType={null}
        autoSize={false}
        resizeHandles={['ne', 'nw', 'se', 'sw']}
        preventCollision={true} // prevents sections mounting on each other.
        onLayoutChange={(newLayout) => {
          const newSection = gridSections.map((section, index) => ({
            ...section,
            ...pick(newLayout[index], [
              'x',
              'y',
              'w',
              'h',
              'isResizable',
              'isDraggable',
            ]),
          }));

          setGridSections(newSection);
          onGridSectionsChange?.(newSection);
        }}>
        {gridSections.map((item: GridLayout.Layout) => {
          const isRecommendationSection =
            item.h === 1 &&
            item.w === 3 &&
            item.x === 0 &&
            item.y === 3 &&
            isRecommendationEnabled;

          return (
            <GridItem
              key={item.i}
              data-testid={`${DataTestID.LayoutBuilderGridSection}-${item.i}`}
              $active={isGridFull ? false : activeSection === item.i}
              $isDrawing={isDrawing}
              data-grid={{
                ...item,
                isResizable: isRecommendationSection
                  ? false
                  : activeSection === item.i,
                isDraggable: isRecommendationSection
                  ? false
                  : isGridFull
                  ? false
                  : activeSection === item.i,
              }}
              $sectionFontSize={sectionFontSize}
              onClick={(e: MouseEvent<HTMLDivElement, MouseEvent>) => {
                e.stopPropagation();
                if (!isDrawing && !isRecommendationSection) {
                  setActiveSection(item.i);
                  onActiveSectionChange?.(item.i);
                }
              }}>
              {isRecommendationSection && lang.editLayoutsSidebar.dynamicArea}
              {activeSection === item.i &&
                !isRecommendationSection &&
                canDeleteCurrentSelectedSection && (
                  <DeleteButton
                    onMouseDown={(e: MouseEvent<HTMLDivElement, MouseEvent>) =>
                      e.stopPropagation()
                    }
                    onMouseUp={(e: MouseEvent<HTMLDivElement, MouseEvent>) =>
                      e.stopPropagation()
                    }
                    onClick={() => {
                      handleDelete(item.i);
                    }}
                    sx={{
                      zoom: dynamicRatio,
                    }}>
                    <DeleteOutlineIcon
                      color={!isScheduleComponent ? 'error' : undefined}
                    />
                  </DeleteButton>
                )}
            </GridItem>
          );
        })}
      </GridLayout>
    </GridContainer>
  );
};
